import { useEffect, useRef } from "react";
import type { IdentitySetOnceProps, IdentitySetProps } from "./types.js";
import {
  aliasUser,
  identifyUser,
  resetAnalytics,
  setOrganizationContext,
  setPartnerContext,
} from "./wrapper.js";

export interface AnalyticsIdentityInput {
  /** True once Amplify reports a confirmed session. */
  readonly isAuthenticated: boolean;
  /** Cognito `sub` (NOT email). */
  readonly userId?: string;
  /** Email — passed in the dedicated `email` argument to `identifyUser`. */
  readonly email?: string;
  readonly setProps?: IdentitySetProps;
  readonly setOnceProps?: IdentitySetOnceProps;
  readonly organizationId?: string;
  readonly organizationProps?: { readonly name?: string; readonly email?: string };
  readonly partnerId?: string;
  /**
   * When provided, calls `aliasUser(email, userId)` exactly once on first
   * sight of an authenticated user. Used by Cockpit/Wallet to merge the
   * Registration-app Person profile (keyed by email) with the
   * Cognito-`sub`-keyed profile. Registration itself sets this to `false`
   * because registration's backend doesn't return the Cognito sub.
   */
  readonly enableAliasFromEmail?: boolean;
}

export interface AnalyticsIdentityProps {
  readonly input: AnalyticsIdentityInput;
}

/**
 * Mount once at the composition root. Reacts to auth-state changes by
 * (a) calling `identifyUser` on transition to authenticated, (b)
 * attaching organization / partner groups, (c) optionally aliasing the
 * email-keyed Registration profile to the Cognito-sub-keyed one on
 * first sight, (d) calling `resetAnalytics()` on sign-out.
 *
 * Apps are expected to derive the `input` shape from
 * `@cakewalk/frontend-auth`'s `useCurrentUser()` hook.
 */
export function AnalyticsIdentity({ input }: AnalyticsIdentityProps) {
  const lastIdentifiedUserId = useRef<string | null>(null);
  const aliasedEmails = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (input.isAuthenticated && input.userId) {
      if (lastIdentifiedUserId.current !== input.userId) {
        identifyUser(input.userId, input.setProps, input.setOnceProps, input.email);
        lastIdentifiedUserId.current = input.userId;
        if (input.enableAliasFromEmail && input.email && !aliasedEmails.current.has(input.email)) {
          aliasUser(input.email, input.userId);
          aliasedEmails.current.add(input.email);
        }
      }
      if (input.organizationId) {
        setOrganizationContext(input.organizationId, input.organizationProps ?? {});
      }
      if (input.partnerId) {
        setPartnerContext(input.partnerId);
      }
    } else if (!input.isAuthenticated && lastIdentifiedUserId.current) {
      resetAnalytics();
      lastIdentifiedUserId.current = null;
    }
  }, [
    input.isAuthenticated,
    input.userId,
    input.email,
    input.organizationId,
    input.organizationProps,
    input.partnerId,
    input.setProps,
    input.setOnceProps,
    input.enableAliasFromEmail,
  ]);

  return null;
}
